/**
 * Copy-to-clipboard button.
 *
 * Copies a caller-supplied value (account id, clerk user id, request id —
 * never a secret) and flashes a check mark. Clipboard access can be denied by
 * the browser (permissions, http contexts); failure degrades to selecting
 * nothing rather than throwing.
 */
import { useEffect, useRef, useState } from 'react';
import { Check, Copy } from '@/components/icons';
import { cn } from '@/lib/utils';

export interface CopyButtonProps {
  /** Value copied to the clipboard. Never pass secrets. */
  value: string;
  /** Accessible label (also used as the tooltip title). */
  label: string;
  className?: string;
}

export function CopyButton({ value, label, className }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable (permission/context). Degrade silently; the raw
      // value is displayed next to the button and can be selected manually.
    }
  }

  return (
    <button
      type="button"
      onClick={() => void handleCopy()}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
    >
      {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
    </button>
  );
}
